import { createFileRoute, Link } from "@tanstack/react-router";
import { makeSeoDescription } from "@/lib/seo-description";

const SITE = "https://lifewestway.com";
const TITLE = "Disclaimer | LifeWestWay";
const DESC =
  "LifeWestWay's content is general information only — not legal, tax, immigration, or financial advice. Read our full disclaimer before relying on any article.";
const URL_ = `${SITE}/disclaimer`;

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:site_name", content: "LifeWestWay" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: makeSeoDescription(DESC) },
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/disclaimer" }],
  }),
  component: DisclaimerPage,
});

function DisclaimerPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
      <header className="mb-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Legal</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-foreground sm:text-5xl">
          Disclaimer
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Last Updated: July 27, 2026
        </p>
      </header>

      <p className="mb-10 text-[15px] leading-relaxed text-foreground/80">
        Please read this disclaimer carefully before using{" "}
        <strong className="text-foreground">LifeWestWay</strong> (the &ldquo;Site&rdquo;) or relying on any
        information published on it.
      </p>

      <div className="space-y-6 text-[15px] leading-relaxed text-foreground/80">
        <section>
          <h2 className="mt-2 font-display text-2xl font-bold text-foreground">No Professional Advice</h2>
          <p className="mt-3">
            The content on this Site — including articles, checklists, downloadable PDFs, case
            studies, and any other material — is provided for general informational and educational
            purposes only. It does not constitute, and should not be relied upon as:
          </p>
          <ul className="mt-3 ml-5 list-disc space-y-2">
            <li>Legal advice or immigration advice of any kind</li>
            <li>Tax advice</li>
            <li>Financial, investment, or banking advice</li>
            <li>A substitute for consultation with a licensed professional</li>
          </ul>
          <p className="mt-3">
            We are not a law firm, registered migration agency, immigration consultancy, tax
            preparer, or financial advisory firm, and no content on this Site creates an
            advisor-client, attorney-client, or fiduciary relationship of any kind.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">Accuracy of Information</h2>
          <p className="mt-3">
            Immigration rules, visa requirements, tax rates, benefit thresholds, and financial
            regulations across the United States, United Kingdom, Australia, Italy, Portugal, and
            Canada change frequently and without notice. While we make reasonable efforts to keep
            our content accurate and up to date, referencing official government sources where
            possible, we cannot guarantee that:
          </p>
          <ul className="mt-3 ml-5 list-disc space-y-2">
            <li>Information reflects the most current rules at the time you read it</li>
            <li>Figures, fees, and thresholds have not changed since publication</li>
            <li>Any specific guidance applies to your individual circumstances</li>
          </ul>
          <p className="mt-3">
            Always verify current requirements directly with the relevant official government agency
            (such as USCIS, UKVI, the Australian Department of Home Affairs, AIMA, IRCC, or the
            equivalent body) or a licensed professional before making any decision based on content
            from this Site.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
            Illustrative Case Studies
          </h2>
          <p className="mt-3">
            Some articles on this Site include &ldquo;case studies,&rdquo; &ldquo;success stories,&rdquo; or
            &ldquo;personal examples.&rdquo; These are clearly marked as illustrative, composite scenarios
            created for educational purposes. They do not depict real, identifiable individuals
            unless explicitly stated otherwise, and any resemblance to real persons is coincidental.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">No Guarantee of Outcomes</h2>
          <p className="mt-3">
            Nothing on this Site guarantees any specific outcome — including visa approval,
            permanent residency, citizenship, employment, loan approval, or any financial result.
            Immigration and financial decisions carry inherent risk and depend on facts specific to
            each individual.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">External Links</h2>
          <p className="mt-3">
            This Site may link to third-party websites, including government agencies, financial
            institutions, and other resources, for your convenience. We do not control and are not
            responsible for the content, accuracy, or privacy practices of any external site.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">Limitation of Liability</h2>
          <p className="mt-3">
            To the fullest extent permitted by law, LifeWestWay and its authors, contributors, and
            affiliates shall not be liable for any direct, indirect, incidental, or consequential
            loss or damage arising from your use of, or reliance on, any information contained on
            this Site.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
            Changes to This Disclaimer
          </h2>
          <p className="mt-3">
            We may update this Disclaimer from time to time. Continued use of the Site after changes
            are posted constitutes your acceptance of the revised Disclaimer.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
            Why Professional Advice Still Matters
          </h2>
          <p className="mt-3">
            Immigration, tax and financial rules are written in general terms, but they are applied
            to particular facts. Two people applying under the same visa category can receive
            different outcomes because of a prior refusal, a gap in employment history, a dependent
            with separate nationality, an unusual source of funds, or a tax residency that overlaps
            two countries in the same year. Nothing published here can account for those variables.
            Where the stakes are high — a refusal that triggers a re-entry ban, a filing position
            that could be treated as under-declaration, or a transfer large enough to attract
            reporting obligations — a licensed immigration adviser, solicitor or accountant
            regulated in the relevant country is the correct source of advice, not a website.
          </p>
          <p className="mt-3">
            We also draw a firm line between explaining a system and operating inside it. We do not
            prepare, review, submit or track applications on anyone&rsquo;s behalf; we do not
            represent readers before any government body; and we do not hold client funds or
            documents. If you receive a message claiming to offer paid application services in the
            name of LifeWestWay, it did not come from us, and we would be grateful if you reported
            it through our contact page so we can warn other readers.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">
            Currency, Figures and Translation
          </h2>
          <p className="mt-3">
            Fees, salary thresholds and cost-of-living figures are quoted in the currency of the
            country being discussed and reflect the published amount at the time of writing.
            Conversions shown for comparison are indicative only, because exchange rates move daily
            and the rate applied by a bank, card issuer or transfer service will differ from any
            mid-market rate quoted here. Where a source document exists only in Italian, Portuguese
            or French, our summary is an editorial translation for comprehension; the original text
            published by the authority remains the only binding version, and you should rely on it
            for any decision with legal or financial consequences.
          </p>
        </section>

        <section>
          <h2 className="mt-10 font-display text-2xl font-bold text-foreground">Questions?</h2>
          <p className="mt-3">
            If you have questions about this Disclaimer, please visit our{" "}
            <Link title="Contact the LifeWestWay editorial team" to="/contact" className="font-semibold text-accent hover:underline">
              Contact Us
            </Link>{" "}
            page.
          </p>
        </section>
      </div>

      <nav aria-label="Breadcrumb" className="mt-14 text-xs text-muted-foreground">
        <Link title="Return to the LifeWestWay homepage" to="/" className="hover:text-accent">
          Home
        </Link>
        <span className="px-2">/</span>
        <span>Disclaimer</span>
      </nav>
    </div>
  );
}
